import React, { Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import AxiosInterceptorSetup from './auth/config/AxiosInterceptorSetup';
import { isMaintenance, isTestComponentAlwaysDisplay, isIosPlatform, isAndroidPlatform } from './config/envConfig';
import LoggedInNotAllowedProtectedRoute from './auth/routes/LoggedInNotAllowedProtectedRoute';
import RoleProtectedRoute from './auth/routes/RoleProtectedRoute';
import { RoutesEnum as AppRoutes } from './routes/RoutesEnum';
import { RolesEnum as Roles } from './auth/helpers/RolesEnum';
import AuthProvider from './auth/contexts/AuthContext';
import CampaignRoutes from './routes/CampaignRoutes';
import ScrollToTopAuto from './components/ScrollToTopAuto';
import useIsGdprConsent from './hooks/useIsGdprConsent';
import { getGdprConsent } from './helpers/CookieConsentHelper';
import { useAuthContext } from './auth/contexts/AuthContext';
import CampaignProvider from './contexts/CampaignContext';

const Home = React.lazy(() => import('./pages/Home'));
const Login = React.lazy(() => import('./auth/pages/Login'));
const Signup = React.lazy(() => import('./auth/pages/Signup'));
const SetNewPassword = React.lazy(() => import('./auth/pages/SetNewPassword'));
const ResetPassword = React.lazy(() => import('./auth/pages/ResetPassword'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Plans = React.lazy(() => import('./pages/Plans'));
const Help = React.lazy(() => import('./pages/Help/Help'));
const Billing = React.lazy(() => import('./pages/Billing'));
const Settings = React.lazy(() => import('./pages/Settings'));
const InstagramUrl = React.lazy(() => import('./pages/campaign/apps/InstagramUrl'));
const RollDice = React.lazy(() => import('./pages/apps/RollDice/RollDice'));
const RandomNumbers = React.lazy(() => import('./pages/apps/RandomNumbers'));
const FlipCoin = React.lazy(() => import('./pages/apps/FlipCoin/FlipCoin'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const Terms = React.lazy(() => import('./pages/Terms'));
const Maintenance = React.lazy(() => import('./pages/Maintenance'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const SetEmailVerify = React.lazy(() => import('./auth/pages/SetEmailVerify'));
const Campaign = React.lazy(() => import('./pages/campaign/Campaign'));
const RedirectHandler = React.lazy(() => import('./pages/RedirectHandler'));
const Apps = React.lazy(() => import('./pages/Apps'));
const Test = React.lazy(() => import('./components/Test'));
const TiktokUrl = React.lazy(() => import('./pages/campaign/apps/TiktokUrl'));
const YoutubeUrl = React.lazy(() => import('./pages/campaign/apps/YoutubeUrl'));
const TwitterXUrl = React.lazy(() => import('./pages/campaign/apps/XTwitterUrl'));
const RandomNameWinnerList = React.lazy(() => import('./pages/campaign/apps/RandomNameWinnerList'));
const CampaignSearchByCodePage = React.lazy(() => import('./pages/apps/CampaignSearchByCodePage'));
const CreateGiveawayImage = React.lazy(() => import('./pages/apps/CreateGiveawayImage'));
const C = React.lazy(() => import('./pages/C'));
const P = React.lazy(() => import('./pages/P'));

// Component that signals when GoogleOAuthProvider is ready
const ProviderReadySignal: React.FC<{ onReady: (ready: boolean) => void }> = ({ onReady }) => {
  React.useEffect(() => {
    onReady(true);
    return () => onReady(false);
  }, [onReady]);
  return null;
};

// Only loads @react-oauth/google after functional consent is confirmed.
// Using useEffect + dynamic import() guarantees the Google Identity Services
// script (accounts.google.com/gsi/client) never loads before consent.
const GoogleOAuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { consent } = useIsGdprConsent();
  const { setIsGoogleProviderReady } = useAuthContext();
  const [Provider, setProvider] = React.useState<React.ComponentType<any> | null>(null);

  React.useEffect(() => {
    if (consent.functional === true && !Provider) {
      import('@react-oauth/google').then(m => {
        setProvider(() => m.GoogleOAuthProvider);
      });
    }
  }, [consent.functional, Provider]);

  if (consent.functional !== true || !Provider) {
    return <>{children}</>;
  }

  return (
    <Provider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID as string}>
      <ProviderReadySignal onReady={setIsGoogleProviderReady} />
      {children}
    </Provider>
  );
};

const AppSP: React.FC = () => {
  const routes = (
    <Suspense fallback={null}>
    <Routes>
      {(isMaintenance) ? (
        <Route path={AppRoutes.MAINTENANCE} element={<Maintenance />} />
      ) : (isTestComponentAlwaysDisplay) ? (
        <Route path={AppRoutes.TEST} element={<Test />} />
      ) : (
        <>
          <Route path={AppRoutes.PLANS} element={<Plans />} />
          <Route path={AppRoutes.HELP} element={<Help />} />
          <Route path={AppRoutes.PRIVACY_POLICY} element={<PrivacyPolicy />} />
          <Route path={AppRoutes.TERMS} element={<Terms />} />
          <Route path={AppRoutes.REDIRECT_HANDLER} element={<RedirectHandler />} />
          <Route path={AppRoutes.SET_EMAIL_VERIFY} element={<SetEmailVerify />} />
          <Route path={AppRoutes.P} element={<P />} />

          {/*apps*/}
          <Route path={AppRoutes.APPS} element={<Apps />} />
          <Route path={AppRoutes.CAMPAIGN_SEARCH_BY_CODE} element={<CampaignSearchByCodePage />} />
          <Route path={AppRoutes.APP_CREATE_GIVEAWAY_IMAGE} element={<CreateGiveawayImage />} />
          <Route element={<CampaignRoutes />}>
            {/*home page has component app, need access to the campaign provider*/}
            <Route element={<LoggedInNotAllowedProtectedRoute />}>
              <Route path={AppRoutes.HOME} element={(isIosPlatform || isAndroidPlatform) ? <Navigate to={AppRoutes.APPS} replace /> : <Home />} />
            </Route>
            <Route path={AppRoutes.C} element={<C />} />
            <Route path={AppRoutes.CAMPAIGN} element={<Campaign />} />
            <Route path={AppRoutes.APP_INSTAGRAM_URL} element={<InstagramUrl />} />
            <Route path={AppRoutes.APP_TIKTOK_URL} element={<TiktokUrl />} />
            <Route path={AppRoutes.APP_YOUTUBE_URL} element={<YoutubeUrl />} />
            <Route path={AppRoutes.APP_TWITTER_URL} element={<TwitterXUrl />} />
            <Route path={AppRoutes.APP_RANDOM_NAME_WINNERS} element={<RandomNameWinnerList />} />
          </Route>
          <Route path={AppRoutes.APP_ROLL_DICE} element={<RollDice />} />
          <Route path={AppRoutes.APP_FLIP_COIN} element={<FlipCoin />} />
          <Route path={AppRoutes.APP_RANDOM_NUMBERS} element={<RandomNumbers />} />
          {/*apps - end*/}

          <Route element={<LoggedInNotAllowedProtectedRoute />}>
            <Route path={AppRoutes.RESET_PASSWORD} element={<ResetPassword />} />
            <Route path={AppRoutes.SET_NEW_PASSWORD} element={<SetNewPassword />} />
            <Route path={AppRoutes.LOGIN} element={<Login />} />
            <Route path={AppRoutes.SIGNUP} element={<Signup />} />
          </Route>

          <Route element={<RoleProtectedRoute roles={[Roles.ADMIN, Roles.USER]} />}>
            <Route path={AppRoutes.DASHBOARD} element={<Dashboard />} />
            <Route path={AppRoutes.BILLING} element={<Billing />} />
            <Route path={AppRoutes.SETTINGS} element={<Settings />} />
          </Route>

          <Route element={<RoleProtectedRoute roles={[Roles.ADMIN]} />}>
            <Route path={AppRoutes.ADMIN_DASHBOARD} element={<AdminDashboard />} />
          </Route>

          <Route path={AppRoutes.NOT_FOUND} element={<NotFound />} />
        </>
      )}
    </Routes>
    </Suspense>
  );

  return (
    <GoogleOAuthGate>
      <AxiosInterceptorSetup />
      {routes}
    </GoogleOAuthGate>
  );
};

export default AppSP;